import type { StaffRole } from '@ucp/web/lib';

/**
 * Roles that hold the `commercial_sites_projection_manage` capability (server
 * capabilities.ts). PRO-0920 (TD-1 retirement) moved this capability to its permanent
 * home `COMMERCIAL_DATA_STEWARD` (the commercial data steward owns the establishment
 * register), retiring the PRO-0820 interim reuse on `ADMISSIONS_DIRECTOR` (ADR-029 A3 /
 * TDA §6). `L6_PLATFORM_ADMIN` is DELIBERATELY absent (ADR-018 decision 3 / EoP — never
 * a commercial shortcut).
 *
 * Dependency-light (no React) so the `AppRoutes` route guard + `navConfig` can import
 * it without pulling the lazy-loaded surface into the initial bundle.
 */
export const COMMERCIAL_SITES_ROLES: readonly StaffRole[] = ['COMMERCIAL_DATA_STEWARD'];

/**
 * Roles that hold `placement_confirm` server-side (PRO-1260 Epic 3 / PA-05 §D).
 * Mirrors CAPABILITIES in apps/api/src/auth/capabilities.ts — the server gate is
 * authoritative; this union only decides what the SPA offers. PLACEMENT_MANAGER is
 * DELIBERATELY absent (it retains placement_manage, not confirm). L6 never appears.
 */
export const COMMERCIAL_PLACEMENT_APPROVAL_ROLES: readonly StaffRole[] = [
  'PRINCIPAL',
  'SCHOOL_BUSINESS_MANAGER',
];

/**
 * Roles that hold `commercial_data_read` (capabilities.ts). This is the data-platform
 * surface (customers, catalogue, price lists), not the whole commercial route union.
 * PRO-3070. `L6` is absent.
 */
export const COMMERCIAL_DATA_READ_ROLES: readonly StaffRole[] = [
  'COMMERCIAL_DATA_STEWARD',
  'PRICING_MANAGER',
  'OPS_DIRECTOR',
  'CONTRACT_MANAGER',
];

/**
 * Roles whose Commercial entry is Placements when they do not hold `commercial_data_read`.
 * Confirmation (`PRINCIPAL`, `SCHOOL_BUSINESS_MANAGER`) and administration
 * (`PLACEMENT_MANAGER`). PRO-3070.
 */
export const COMMERCIAL_PLACEMENT_ENTRY_ROLES: readonly StaffRole[] = [
  'PRINCIPAL',
  'SCHOOL_BUSINESS_MANAGER',
  'PLACEMENT_MANAGER',
];

export type CommercialEntryPath = '/commercial/customers' | '/commercial/placements';

/**
 * Where this caller should open Commercial. Data-platform readers go to customers.
 * Placement confirmers and placement managers go to placements when contracts are on.
 * Anyone else, including a referrals-only user, gets null (the module stays hidden).
 */
export function commercialEntryPath(
  user: { readonly roles: readonly { readonly role: StaffRole }[] },
  flags: { readonly commercialContractsEnabled?: boolean },
): CommercialEntryPath | null {
  const holds = (roles: readonly StaffRole[]) =>
    user.roles.some((assignment) => roles.includes(assignment.role));
  if (holds(COMMERCIAL_DATA_READ_ROLES)) {
    return '/commercial/customers';
  }
  if (flags.commercialContractsEnabled === true && holds(COMMERCIAL_PLACEMENT_ENTRY_ROLES)) {
    return '/commercial/placements';
  }
  return null;
}

/**
 * Route-guard union for `/commercial/*` (PRO-0510, ADR-029). Wider than
 * `COMMERCIAL_DATA_READ_ROLES`: placement roles can open the module, and the entry
 * path (PRO-3070) decides whether they arrive on customers or placements.
 * `BILLING_MANAGER` is still absent. `L6` is absent. Dependency-light (no React).
 */
export const COMMERCIAL_DATA_ROLES: readonly StaffRole[] = [
  'COMMERCIAL_DATA_STEWARD',
  'PRICING_MANAGER',
  'OPS_DIRECTOR',
  'PRINCIPAL',
  'SCHOOL_BUSINESS_MANAGER',
  'PLACEMENT_MANAGER',
  // PRO-1260 Epic 2 — worklist holders (TIP: add CONTRACT_MANAGER if absent).
  'CONTRACT_MANAGER',
];
